import { ReactNode, createContext, useContext, useMemo } from 'react'

import { EmbroiderySelectionInitialState } from './providers/EmbroiderySelectionProvider'
import { LogoFile } from './types'
import { useTransientLogo } from './useEmbroideryLogos'
import { firstLogoItem } from './utils/buildV2EmbroideryLineSet'

/**
 * The new logo another garment in this order carries. An order may carry only one, so while this
 * exists the garment being edited can choose it or a digitized logo, but can't upload another.
 */
export type NewLogoInOrder = {
  logoFile: LogoFile
  /** Whether that other garment pays for the digitizing, so this one isn't charged for it again. */
  isSetupChargedElsewhere: boolean
}

const NewLogoOnPageContext = createContext<NewLogoInOrder | undefined>(undefined)

/** Hands down the new logo on another kit piece or bulk tile that isn't in the cart yet. */
export const NewLogoOnPageProvider = ({
  value,
  children,
}: {
  value: NewLogoInOrder | undefined
  children?: ReactNode
}) => <NewLogoOnPageContext.Provider value={value}>{children}</NewLogoOnPageContext.Provider>

/**
 * The new logo another garment in this order already carries: the cart's, else one on another kit
 * piece or bulk tile. The garment being edited never counts against itself.
 *
 * Use it for what the logo form offers and what prices show. Bag lines use `useTransientLogo`
 * instead: another garment on the page may never reach the bag, so it can't cover this one's setup.
 */
export function useNewLogoInOrder(): NewLogoInOrder | undefined {
  const transientLogo = useTransientLogo()
  const newLogoOnPage = useContext(NewLogoOnPageContext)

  return useMemo(
    () =>
      transientLogo ? { logoFile: transientLogo, isSetupChargedElsewhere: true } : newLogoOnPage,
    [newLogoOnPage, transientLogo]
  )
}

/** The new logo one garment's embroidery carries. */
export type GarmentNewLogo = {
  logoFile: LogoFile
  /** Whether it goes in the bag with the garment: confirmed, and not unchecked. */
  goesInBag: boolean
}

/** The new logo a garment's selection carries, if its logo still needs digitizing. */
export const newLogoOfSelection = ({
  items,
  confirmed,
}: EmbroiderySelectionInitialState): GarmentNewLogo | undefined => {
  const logoItem = firstLogoItem(items)
  const logoFile = logoItem?.values.logoFile
  if (!logoItem?.values.requiresSetup || !logoFile) return undefined

  return { logoFile, goesInBag: confirmed && logoItem.enabledForBag }
}
